import React from 'react';
import Image from 'next/image';
import { cn } from '../../utils/cn';
import type { NoDataProps } from './no-data.types';

/**
 * NoData - Empty state component
 *
 * Displays a friendly empty state when there's no data to show.
 * Commonly used in lists, search results, and data tables.
 *
 * ### Key Features
 * - **Customizable Message**: Main text and description
 * - **Image Support**: Default or custom empty state image
 * - **Action Button**: Optional CTA for user action
 * - **Flexible Styling**: Custom colors and spacing
 * - **Responsive**: Adapts to container size
 */
const NoData: React.FC<NoDataProps> = ({
    text = 'No data available',
    description,
    backgroundColor,
    textColor,
    gap = 24,
    image,
    imageSize = 200,
    action,
    className,
    testID = 'no-data',
}) => {
    const isImageUrl = typeof image === 'string';

    return (
        <div
            data-testid={testID}
            className={cn('flex flex-col items-center justify-center py-12 px-4', className)}
            style={{
                backgroundColor,
                gap: `${gap}px`,
            }}
        >
            {/* Empty State Image */}
            <div className="relative flex items-center justify-center" style={{ width: imageSize, height: imageSize }}>
                {(() => {
                    if (isImageUrl) {
                        return <Image src={image} alt="No data" fill className="object-contain" />;
                    }
                    if (image) {
                        return image;
                    }
                    // Default SVG illustration
                    return (
                        <svg viewBox="0 0 200 200" fill="none" className="w-full h-full">
                            {/* Empty folder illustration */}
                            <rect x="40" y="80" width="120" height="90" rx="8" fill="#E0E0E0" />
                            <path
                                d="M40 95C40 90.5817 43.5817 87 48 87H92L102 77H152C156.418 77 160 80.5817 160 85V95H40Z"
                                fill="#BDBDBD"
                            />
                            <circle cx="100" cy="125" r="25" fill="#F5F5F5" />
                            <path d="M100 115V135M90 125H110" stroke="#9E9E9E" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                    );
                })()}
            </div>

            {/* Text Content */}
            <div className="flex flex-col items-center gap-2 max-w-md text-center">
                <h3 data-testid="no-data-text" className="text-lg font-semibold" style={{ color: textColor }}>
                    {text}
                </h3>

                {description && (
                    <p className="text-sm text-neutral-60" style={{ color: textColor ? `${textColor}99` : undefined }}>
                        {description}
                    </p>
                )}
            </div>

            {/* Action Button */}
            {action && <div className="mt-2">{action}</div>}
        </div>
    );
};

export default NoData;
